// Age advancement, now gated by the era boss. Crossing the next progress
// threshold no longer hands a card directly: it summons that era's BOSS to the
// gate (see systems/waves.ts). Only defeating the gate boss opens the card
// choice, which advances the age. Losing to it ends the run like any wave.

import type { GameState } from '@core/state';
import { recomputeModifiers } from '@core/state';
import { BALANCE } from '@data/balance';
import { CARD_POOL, CARD_BY_ID, activeSynergies, type CardCategory } from '@data/cards';
import { gateBossName } from '@data/enemies';
import { eraIndex } from '@data/eras';
import { ageLore } from '@data/lore';
import { bus } from '@core/events';
import { t, tc } from '@i18n/i18n';
import { checkConquest } from './prestige';

/** Progress required to advance from `age` to `age + 1`. */
export function ageThreshold(age: number): number {
  return BALANCE.ages.baseThreshold * Math.pow(BALANCE.ages.thresholdGrowth, age);
}

/** Draws one card from each direction so every advance is a genuine choice. */
function drawCards(): string[] {
  const categories: CardCategory[] = ['economy', 'military', 'knowledge'];
  return categories.map((cat) => {
    const pool = CARD_POOL[cat];
    return pool[Math.floor(Math.random() * pool.length)].id;
  });
}

/** Checks whether the settlement has earned its age gate and, if so, summons the
 *  era boss to the gate. This does NOT pause the sim or hand a card: the boss
 *  becomes the next wave, and only defeating it (in waves.ts) opens the reward. */
export function checkGate(state: GameState): void {
  const run = state.run;
  if (state.pending.cardChoices) return; // reward already open
  if (run.gatePending) return; // boss already at the gate
  if (run.age >= BALANCE.ages.maxAge) return; // age cap: no further gates
  if (run.progress < ageThreshold(run.age)) return;

  if (run.age < BALANCE.ages.firstGateAge) {
    // Onboarding: early ages advance freely (no boss). Open the card directly so
    // the player enters their first real gate already holding a card.
    openGateReward(state);
    return;
  }

  // A gate boss may never be a settlement's FIRST fight. Gate bosses are exempt
  // from the onboarding softener by design, so a run that reaches its first gate
  // before its first raider faces an unsoftened boss with a starting defense and
  // dies without ever having played: the same silent game-over that the wave-1
  // grace was added to kill, arriving through the gate instead of the raider.
  // Progress is not lost, the summon just waits for the first raider to be
  // repelled. A no-op at every later gate, where waves have long since started.
  if (run.wave < 1) return;

  // The era boss is summoned to the gate. It becomes the next wave; only beating
  // it opens the card choice (handled in systems/waves.ts).
  run.gatePending = true;
  bus.emit('gate:summon', {
    age: run.age,
    era: eraIndex(run.age),
    boss: gateBossName(run.age),
  });
}

/** Opens the age-advance card choice after the era gate boss is defeated. Pauses
 *  the sim until the player picks (handled by pickCard). Called from waves.ts. */
export function openGateReward(state: GameState): void {
  const cards = drawCards();
  state.pending.cardChoices = cards;
  bus.emit('age:advance', {
    age: state.run.age + 1,
    cards: cards.map((id) => CARD_BY_ID[id]),
  });
}

/** Commits the player's card pick, applies it for the rest of the run, and
 *  advances the age (the gate boss is already beaten by this point). */
export function pickCard(state: GameState, cardId: string): void {
  if (!state.pending.cardChoices || !state.pending.cardChoices.includes(cardId)) return;
  const card = CARD_BY_ID[cardId];
  if (!card) return;

  // Synergies active before vs after this pick, so we can celebrate new ones.
  const before = new Set(activeSynergies(state.run.pickedCards).map((s) => s.id));

  state.run.pickedCards.push(cardId);
  recomputeModifiers(state.run);
  state.run.age += 1;
  state.meta.bestAge = Math.max(state.meta.bestAge, state.run.age);
  state.pending.cardChoices = null;

  // The gate is cleared: reset the boss latch and the within-age raider ramp so
  // the new era starts from its own (higher) threat band.
  state.run.gatePending = false;
  state.run.wavesThisAge = 0;

  bus.emit('card:picked', { card });
  bus.emit('milestone', {
    title: t('milestone.ageDawns', { n: state.run.age + 1 }), // 1-indexed like the HUD age label
    subtitle: ageLore(state.run.age), // the age's narrative beat (story thread)
    tone: 'epic',
  });

  // A newly crossed synergy threshold is a power spike worth announcing.
  for (const syn of activeSynergies(state.run.pickedCards)) {
    if (!before.has(syn.id)) {
      bus.emit('milestone', {
        title: t('milestone.synergy', { name: tc('synergy.' + syn.id + '.name', syn.name) }),
        subtitle: tc('synergy.' + syn.id + '.desc', syn.desc),
        tone: 'epic',
      });
    }
  }

  // Advancing an age may cross the planet's (age-based) conquest goal.
  checkConquest(state);
}
